import { discountsApi } from 'api/discounts'
import { MainTitle } from 'components/ui'
import { MobileFilterTitle } from 'components/ui/MobileFilterTitle/MobileFilterTitle'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { EDiscountStatus, IDiscount } from 'models/discounts'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cleanObject } from 'shared/utils/cleanObject'
import { showServerError } from 'shared/utils/showServerError'
import { useNavigate, useSearchParams } from 'react-router-dom'
import qs from 'qs'
import Table, { ColumnsType } from 'antd/es/table'
import {
  Button,
  Checkbox,
  Input,
  InputRef,
  Popconfirm,
  Radio,
  Space,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'
import moment from 'moment'
import {
  discountsStatuses,
  getDiscountDisplayStatus,
  getDiscountStatusLabel
} from 'shared/constants/discounts'
import {
  FilterFilled,
  InboxOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { adminRoutes, superAdminRoutes } from 'shared/constants/routes'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { ERoles } from 'models/auth'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { appSelectors } from 'store/app'
import styles from './DiscountsList.module.scss'

interface IDiscountsListProps {
  // карточка продавца у супер-админа: только его скидки
  sellerId?: string
}

export const DiscountsList: React.FC<IDiscountsListProps> = ({ sellerId }) => {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()

  const role = useAppSelector(appSelectors.selectRole)
  const isSuperAdmin = useMemo(() => role === ERoles.ADMIN, [role])

  const [discounts, setDiscounts] = useState<IDiscount[]>([])
  const [isFirstRequestFinished, setIsFirstRequestFinished] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 100

  const [nameSearch, setNameSearch] = useState('')
  const nameSearchRef = useRef<InputRef>(null)

  const [status, setStatus] = useState<EDiscountStatus | undefined>(undefined)
  const [showArchived, setShowArchived] = useState(false)

  const getDiscounts = async (
    skip: number,
    status: EDiscountStatus | undefined,
    name?: string
  ) => {
    try {
      setIsLoading(true)
      const response = await discountsApi.getDiscounts({
        skip: String(skip),
        limit: String(pageSize),
        name: name ?? nameSearch ?? undefined,
        status,
        seller_id: sellerId
      })
      setTotal(response.data.count)
      setDiscounts(response.data.items)
      setSearchParams(
        cleanObject({
          page: skip / pageSize + 1 === 1 ? '' : String(skip / pageSize + 1),
          status: status ?? '',
          name: name ?? nameSearch
        })
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (window.location.search) {
      const params = qs.parse(window.location.search.substring(1))
      if (params.page) {
        setCurrentPage(Number(params.page))
      }
      if (params.status) {
        setStatus(params.status as EDiscountStatus)
      }
      if (params.name) {
        setNameSearch(params.name as string)
      }
    }
    setIsFirstRequestFinished(true)
  }, [])

  useEffect(() => {
    if (isFirstRequestFinished) {
      getDiscounts((currentPage - 1) * pageSize, status)
      setCurrentPage(currentPage)
    }
  }, [isFirstRequestFinished])

  const onPageChange = (current: number) => {
    getDiscounts((current - 1) * pageSize, status, nameSearch)
    setCurrentPage(current)
  }

  const saveFieldValue = (name: string) => {
    getDiscounts(0, status, name)
    setCurrentPage(1)
  }

  const onChangeStatus = (newStatus: EDiscountStatus | undefined) => {
    getDiscounts(0, newStatus)
    setCurrentPage(1)
  }

  const archiveDiscount = async (id: string) => {
    try {
      await discountsApi.changeDiscountStatus(id, {
        status: EDiscountStatus.ARCHIVED
      })
      setDiscounts((prev) =>
        prev.map((el) =>
          el._id === id ? { ...el, status: EDiscountStatus.ARCHIVED } : el
        )
      )
      message.success('Скидка отправлена в архив, цены товаров восстановлены')
    } catch (e) {
      showServerError(e)
    }
  }

  const columns: ColumnsType<IDiscount> = useMemo(() => {
    // seller_id приходит с populate — у админа показываем название магазина
    const sellerColumn: ColumnsType<IDiscount>[0] = {
      title: 'Продавец',
      dataIndex: 'seller_id',
      width: 220,
      render: (_, { seller_id }) => {
        const seller = typeof seller_id === 'object' ? seller_id : undefined
        const shopName = seller?.shop_details?.shop_name
        return seller?._id && shopName ? (
          <CardLink to={getSellerEditPath(seller._id)}>{shopName}</CardLink>
        ) : (
          <Typography.Text style={{ opacity: 0.5 }}>
            Удаленный аккаунт
          </Typography.Text>
        )
      }
    }

    const baseColumns: ColumnsType<IDiscount> = [
      {
        title: 'Название',
        dataIndex: 'name',
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Input
                ref={nameSearchRef}
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                onPressEnter={() => {
                  saveFieldValue(nameSearch)
                  close()
                }}
                placeholder='Название скидки'
                className='filterInput'
              />
              <div className='filterFooterButtons'>
                <Button
                  type='primary'
                  onClick={() => {
                    saveFieldValue(nameSearch)
                    close()
                  }}
                  icon={<SearchOutlined />}
                  size='small'
                  className='filterButton'
                >
                  Найти
                </Button>
                <Button
                  onClick={() => {
                    setNameSearch('')
                    saveFieldValue('')
                    close()
                  }}
                  size='small'
                  className='filterButton'
                >
                  Очистить
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Закрыть
                </Button>
              </div>
            </div>
          )
        },
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => nameSearchRef.current?.select(), 100)
          }
        },
        filterIcon: () => (
          <SearchOutlined
            style={{
              color: nameSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
            }}
          />
        )
      },
      {
        title: 'Скидка',
        dataIndex: 'discount_value',
        render: (_, { discount_value }) => {
          return <Typography.Text>{discount_value}%</Typography.Text>
        }
      },
      {
        title: 'Срок действия',
        dataIndex: 'date',
        render: (_, { start_date, end_date }) => {
          return (
            <Typography.Text>
              {moment(start_date).format('DD.MM.YYYY')}
              {' - '}
              {moment(end_date).format('DD.MM.YYYY')}
            </Typography.Text>
          )
        }
      },
      {
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Товаров со скидкой
            <Tooltip title='Товары со скидкой, которые можно купить сейчас: опубликованы и есть на складе. Закончившиеся, снятые с продажи и удалённые не считаются, число пересчитывается при каждом открытии страницы. У запланированной скидки — сколько товаров попадёт под неё, если она начнётся сейчас.'>
              <QuestionCircleOutlined
                style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }}
              />
            </Tooltip>
          </span>
        ),
        dataIndex: 'products_count',
        render: (_, { products_count, applied_product_ids, is_applied }) => {
          // бэкенд без живого счётчика: показываем размер набора, как раньше
          if (products_count === undefined) {
            return (
              <Typography.Text>
                {is_applied ? applied_product_ids?.length ?? 0 : '—'}
              </Typography.Text>
            )
          }
          if (!products_count) return <Typography.Text>—</Typography.Text>
          return (
            <Tooltip
              title={
                is_applied
                  ? `Скидка применена к товарам: ${products_count.total}. Сейчас в продаже: ${products_count.on_sale}.`
                  : `Скидка ещё не применена. Сейчас под неё попадает товаров: ${products_count.total}, из них в продаже: ${products_count.on_sale}.`
              }
            >
              <Typography.Text>{products_count.on_sale}</Typography.Text>
            </Tooltip>
          )
        }
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        render: (_, record) => {
          const displayStatus = getDiscountDisplayStatus(record)
          return (
            <Tag
              color={discountsStatuses[displayStatus].color}
              onClick={(event) => event.stopPropagation()}
            >
              {getDiscountStatusLabel(displayStatus, record.start_date)}
            </Tag>
          )
        },
        filterDropdown: ({ close }) => {
          return (
            <div
              className='filterWrapper'
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Radio.Group
                onChange={(e) => {
                  setStatus(e.target.value)
                  onChangeStatus(e.target.value)
                  close()
                }}
                value={status}
                style={{ display: 'block', marginBottom: 15 }}
              >
                <Space direction='vertical' size={15}>
                  {[
                    discountsStatuses[EDiscountStatus.ACTIVE],
                    discountsStatuses[EDiscountStatus.ARCHIVED]
                  ].map((item) => (
                    <Radio key={item.value as string} value={item.value}>
                      <Tag color={item.color}>{item.label}</Tag>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
              <Button
                onClick={() => {
                  setStatus(undefined)
                  onChangeStatus(undefined)
                  close()
                }}
                size='small'
                className='filterButton'
              >
                Очистить
              </Button>
              <Button
                type='link'
                size='small'
                onClick={() => {
                  close()
                }}
              >
                Закрыть
              </Button>
            </div>
          )
        },
        filterIcon: () => (
          <FilterFilled
            style={{
              color: status ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
            }}
          />
        )
      },
      {
        title: '',
        dataIndex: 'archive',
        width: 60,
        render: (_, { _id, status }) =>
          status !== EDiscountStatus.ARCHIVED && (
            <Popconfirm
              title='Отправить скидку в архив?'
              description='Цены товаров вернутся к значениям до скидки.'
              okText='В архив'
              cancelText='Отмена'
              onConfirm={() => archiveDiscount(_id)}
            >
              {/* stopPropagation — клик по кнопке не должен открывать скидку */}
              <Button
                type='primary'
                danger
                icon={<InboxOutlined />}
                size='small'
                onClick={(event) => event.stopPropagation()}
              />
            </Popconfirm>
          )
      }
    ]

    if (isSuperAdmin && !sellerId) {
      const [nameColumn, discountColumn, ...otherColumns] = baseColumns
      return [nameColumn, discountColumn, sellerColumn, ...otherColumns]
    }

    return baseColumns
  }, [discounts, nameSearch, status, isSuperAdmin, sellerId])

  // Рендеры мобильных колонок берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<IDiscount> = useMemo(() => {
    const findColumn = (dataIndex: string) =>
      columns.find((column) => (column as any).dataIndex === dataIndex)

    const nameColumn = findColumn('name')
    const valueColumn = findColumn('discount_value')
    const dateColumn = findColumn('date')
    const productsColumn = findColumn('products_count')
    const sellerColumn = findColumn('seller_id')
    const statusColumn = findColumn('status')
    const archiveColumn = findColumn('archive')

    return [
      {
        ...nameColumn,
        title: (
          <MobileFilterTitle
            title='Скидка'
            columns={[nameColumn]}
            isActive={Boolean(nameSearch)}
          />
        ),
        width: '62%',
        filterDropdown: undefined,
        filterIcon: undefined,
        render: (value, record, index) => (
          <div className={styles.mobileCell}>
            <span className={styles.mobileName}>{record.name}</span>
            <span className={styles.mobileSubText}>
              {valueColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
            <span className={styles.mobileSubText}>
              {dateColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
            {sellerColumn && (
              <span className={styles.mobileSubText}>
                {sellerColumn.render?.(value, record, index) as React.ReactNode}
              </span>
            )}
            <span className={styles.mobileSubText}>
              Товаров:{' '}
              {productsColumn?.render?.(value, record, index) as React.ReactNode}
            </span>
          </div>
        )
      },
      {
        ...statusColumn,
        title: (
          <MobileFilterTitle
            title='Статус'
            columns={[statusColumn]}
            isActive={Boolean(status)}
          />
        ),
        width: '38%',
        filterDropdown: undefined,
        filterIcon: undefined,
        render: (value, record, index) => (
          <div className={styles.mobileStatusCell}>
            {statusColumn?.render?.(value, record, index) as React.ReactNode}
            {archiveColumn?.render?.(value, record, index) as React.ReactNode}
          </div>
        )
      }
    ]
  }, [columns, nameSearch, status])

  // Архивные скрыты по умолчанию (как в промокодах); явный фильтр по статусу имеет приоритет
  const displayedDiscounts = useMemo(() => {
    if (showArchived || status) return discounts
    return discounts.filter((el) => el.status !== EDiscountStatus.ARCHIVED)
  }, [discounts, showArchived, status])

  return (
    <div>
      <div className={styles.header}>
        <MainTitle text='Скидки' style={{ marginBottom: 0 }} hideOnMobile />
        <div className={styles.headerControls}>
          <Checkbox
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          >
            Показать архивные
          </Checkbox>
          <Button
            type='primary'
            size='large'
            icon={<PlusOutlined />}
            onClick={() =>
              navigate(
                isSuperAdmin && sellerId
                  ? superAdminRoutes.DISCOUNTS_CREATE.replace(':sellerId?', sellerId)
                  : adminRoutes.DISCOUNTS_CREATE
              )
            }
          >
            Создать скидку
          </Button>
        </div>
      </div>
      <Table
        scroll={isMobile ? undefined : { x: 950 }}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={displayedDiscounts}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName='tableRow'
        onRow={(record) => ({
          onClick: () =>
            navigate(adminRoutes.DISCOUNTS_EDIT.replace(':id', record._id))
        })}
        pagination={{
          current: currentPage,
          onChange: onPageChange,
          pageSize,
          total,
          showSizeChanger: false
        }}
      />
    </div>
  )
}
